/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Sign Up Page - Velocity PPM Enterprise Style
 * 
 * Provides user registration with email/password and social login options
 * (Google, Microsoft) based on platform configuration.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, Mail, Lock, User, Loader2 } from 'lucide-react';
import type { RegisterRequestData } from '@shared/types/requests/auth';
import type { SocialProvider as SocialProviderType } from '@shared/types/auth';
import type { AuthProvidersResponseData } from '@shared/types/responses/config';
import { createQueryOptions } from '@shared/lib/api';
import { useQuery } from '@tanstack/react-query';
import { getProviderLogo } from '@shared/components/auth';

interface SocialProvider {
  id: SocialProviderType;
  name: string;
  icon: React.ReactNode;
  available: boolean;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const { register, socialLogin, isLoading, isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState<RegisterRequestData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    authProvider: 'local'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Get available authentication providers from config API
  const { data: authProviders, isLoading: providersLoading } = useQuery(
    createQueryOptions<AuthProvidersResponseData>('/api/config/auth/providers')
  );

  // Convert API providers to UI providers with icons
  const socialProviders: SocialProvider[] = React.useMemo(() => {
    if (!authProviders) return [];

    return authProviders
      .filter(provider => provider.id === 'google' || provider.id === 'microsoft')
      .map(provider => ({
        id: provider.id as SocialProviderType,
        name: provider.name,
        available: provider.enabled,
        icon: getProviderLogo(provider.id)
      }));
  }, [authProviders]);

  const handleInputChange = (field: keyof RegisterRequestData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!formData.name?.trim()) {
      return 'Name is required';
    }
    if (!formData.email?.trim()) {
      return 'Email is required';
    }
    if (!formData.password) {
      return 'Password is required';
    }
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      await register(formData);
      toast({
        title: 'Welcome to Velocity PPM!',
        description: 'Your account has been created successfully.',
      });
      navigate('/');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      toast({
        title: 'Registration failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleSocialLogin = async (provider: SocialProviderType) => {
    setError(null);

    try {
      await socialLogin(provider);
      toast({
        title: 'Welcome to Velocity PPM!',
        description: 'Your account has been created successfully.',
      });
      navigate('/');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      toast({
        title: 'Registration failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const availableProviders = socialProviders.filter(p => p.available);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '32px 32px 24px',
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#0066cc',
            marginBottom: '8px'
          }}>
            Velocity PPM
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '4px'
          }}>
            Product & Project Management
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            marginTop: '16px'
          }}>
            Create your account
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '32px' }}>
          {/* Social Login Buttons */}
          {(providersLoading || availableProviders.length > 0) && (
            <div style={{ marginBottom: '24px' }}>
              {providersLoading && (
                <button 
                  className="btn"
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: '#f0f0f0',
                    color: '#666'
                  }}
                  disabled
                >
                  <Loader2 size={18} className="spin" />
                  Checking authentication providers...
                </button>
              )}
              {availableProviders.map((provider) => (
                <button
                  key={provider.id}
                  className="btn"
                  style={{
                    width: '100%',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: 'white',
                    color: '#333',
                    border: '1px solid #ddd'
                  }}
                  onClick={() => handleSocialLogin(provider.id)}
                  disabled={isLoading || providersLoading}
                >
                  {provider.icon}
                  <span>Continue with {provider.name}</span>
                </button>
              ))}
              
              {availableProviders.length > 0 && (
                <div style={{
                  position: 'relative',
                  margin: '24px 0',
                  textAlign: 'center'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: '#e0e0e0'
                  }} />
                  <span style={{
                    position: 'relative',
                    backgroundColor: 'white',
                    padding: '0 12px',
                    fontSize: '12px',
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Or continue with email
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c33',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="name" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                Full Name
              </label>
              <div style={{ position: 'relative' }}>
                <User style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} size={18} />
                <input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 42px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} size={18} />
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 42px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} size={18} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 42px 12px 42px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#999'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p style={{
                fontSize: '12px',
                color: '#999',
                marginTop: '6px'
              }}>
                Must be at least 8 characters
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} size={18} />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 42px 12px 42px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#999'
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn"
              style={{
                width: '100%',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          textAlign: 'center',
          backgroundColor: '#fafafa',
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Already have an account?{' '}
            <Link 
              to="/signin"
              style={{
                color: '#0066cc',
                textDecoration: 'none',
                fontWeight: '600'
              }}
              onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

